import React, {useState} from 'react';

import classNames from 'classnames';

import {DataInfo, IDataInfoProps} from './../../data-info';
import {Dropdown} from './../../dropdown';
import {ITableRowProps, TableRow, TTableRowId} from './table-row';
import {Icon} from '../../icon/icon';
import { Scroll } from '../../scroll-fade';

export interface ITableProps {
    rows: ITableRowProps[] | IDataInfoProps;
    className?: string;
    columns?: ITableColumnProps[];
    defaultOpenItems?: TTableRowId[];
    focusOnClick?: boolean;
    openItems?: TTableRowId[];
    onChange?: (id: TTableRowId) => void;
    useHover?: boolean;
    limitHeight?: boolean;
    isExpanded?: boolean;
}

export interface ITableColumnProps {
    header: string;
    isFocused?: boolean;
    tooltip?: string | JSX.Element;
    width?: string;
    isSortable?: boolean;
}

export interface ITableCellProps {
    content: TTableCell;
    comparableValue?: string | number;
    tooltip?: string | JSX.Element;
}

interface ColumnOrder {
    order: 'asc' | 'desc';
    index: number;
}

export type TTableCell = string | number | JSX.Element | undefined;

export interface ITableHover {
    id?: TTableRowId;
    column?: number;
}

export const Table = (props: ITableProps): JSX.Element => {
    const [openItems, setOpenItems] = useState<TTableRowId[]>(props.defaultOpenItems || []);
    const [hover, setHover] = useState<ITableHover | null>(null);
    const [columnOrder, setColumnOrder] = useState<ColumnOrder | null>(null);
    const isControlled: boolean = typeof props.openItems !== 'undefined';
    const getValue: (inputValue?: TTableRowId[]) => TTableRowId[] = (inputValue?: TTableRowId[]): TTableRowId[] => {
        if (!inputValue) {
            return [];
        }

        return inputValue;
    };
    const controlledValue: TTableRowId[] = getValue(isControlled ? props.openItems : openItems);
    const focusedColumns: number[] = [];
    const className: string = classNames(
        'table',
        props.className,
    );

    const hasTableData = (data: ITableRowProps[] | IDataInfoProps): data is ITableRowProps[] => (data as ITableRowProps[]).length > 0;

    const toggleRow: (id: TTableRowId) => void = (id: TTableRowId) => {
        if (!isControlled) {
            if (openItems.includes(id)) {
                setOpenItems(openItems.filter((el: TTableRowId) => el !== id));
            } else {
                setOpenItems([...openItems, id]);
            }
        }

        if (props.onChange) {
            props.onChange(id);
        }
    };

    const checkIfSiblingHasChildren: (rows: ITableRowProps[] | undefined) => boolean = (rows: ITableRowProps[] | undefined) => (
        rows ? rows.filter((el: ITableRowProps) => el.subRows && el.subRows.length > 0).length > 0 : false
    );

    const isHoveringRow = (rowId: TTableRowId): boolean => {
        if (hover && typeof hover.id !== 'undefined') {
            return rowId === hover.id;
        }

        return false;
    };

    const getHoveringColumn = (): number | undefined => {
        if (props.useHover && hover && typeof hover.column !== 'undefined') {
            return hover.column;
        }

        return undefined;
    };

    const renderRow: (
        row: ITableRowProps,
        index: TTableRowId,
        depth: number,
        isVisible: boolean,
        siblingHasSubRows: boolean,
    ) => JSX.Element = (
        row: ITableRowProps,
        index: TTableRowId,
        depth: number,
        isVisible: boolean,
        siblingHasSubRows: boolean) => {
        const controller: TTableRowId = row.id ? row.id : index;
        const subRowSiblingHasChildren: boolean = row.subRows ? checkIfSiblingHasChildren(row.subRows) : false;

        return (
            <React.Fragment key={'fragment-' + index}>
                <TableRow
                    {...row}
                    depth={depth}
                    focusedColumns={focusedColumns}
                    hoveringColumn={getHoveringColumn()}
                    id={controller}
                    isHovering={props.useHover && isHoveringRow(controller)}
                    isFocused={props.focusOnClick ? controlledValue.includes(controller) : row.isFocused}
                    isOpen={controlledValue.includes(controller)}
                    isVisible={isVisible}
                    key={index}
                    onClick={row.subRows ? (): void => toggleRow(controller) : undefined}
                    onHover={(id?: TTableRowId, column?: number): void => {
                        if (props.useHover) {
                            setHover({id, column});
                        }
                    }}
                    siblingHasChildren={siblingHasSubRows}
                />
                {row.subRows && controlledValue.includes(controller) && isVisible && row.subRows.map((subRow: ITableRowProps, i: number) => (
                    renderRow(subRow, index + '-' + i, depth + 1, controlledValue.includes(controller) && isVisible, subRowSiblingHasChildren)
                ))}
            </React.Fragment>
        );
    };

    const sortedTableRows = (rows : ITableRowProps[]): ITableRowProps[] => {
        if (columnOrder === null) {
            return rows;
        } else if (columnOrder.order === 'asc') {
            return [...rows].sort((a, b) => a.cells[columnOrder.index].comparableValue! > b.cells[columnOrder.index].comparableValue! ? 1 : -1);
        } else {
            return [...rows].sort((a, b) => a.cells[columnOrder.index].comparableValue! > b.cells[columnOrder.index].comparableValue! ? -1 : 1);
        }
    };

    const onSortClick = (column: ITableColumnProps, i: number): void => {
        if (!column.isSortable) {
            return;
        } else if (columnOrder === null) {
            setColumnOrder({
                index: i,
                order: 'asc',
            });
        } else {
            if (columnOrder?.index === i) {
                setColumnOrder({
                    index: i,
                    order: columnOrder.order === 'asc' ? 'desc' : 'asc',
                });
            } else {
                setColumnOrder({
                    index: i,
                    order: 'asc',
                });
            }
        }
    };

    const renderTable = (): JSX.Element => (
        <div className={className}>
            {hasTableData(props.rows) ?
                <table className="table__table">
                    {props.columns &&
                        <thead>
                            <tr className="table__row">
                                {props.columns.map((column: ITableColumnProps, i: number) => {
                                    const headerClassName: string = classNames(
                                        'table__header',
                                        {
                                            'is-focused': column.isFocused,
                                            'is-hovering': !!(props.useHover && getHoveringColumn() === i),
                                        },
                                    );

                                    if (column.isFocused) {
                                        focusedColumns.push(i);
                                    }

                                    const chooseIcon = (): JSX.Element => {
                                        if (columnOrder?.order === 'asc') {
                                            return <Icon name={'triangle-down'} className={'table__icon_column_header'}/>;
                                        } else {
                                            return <Icon name={'triangle-up'} className={'table__icon_column_header'}/>;
                                        }
                                    };

                                    return (
                                        <td key={'header' + i} className={headerClassName} width={column.width} onClick={(): void => onSortClick(column, i)}>
                                            {column.tooltip ? cellTooltip(column.header, column.tooltip) : column.header}
                                            {columnOrder?.index === i && column.isSortable ? chooseIcon() : undefined}
                                        </td>
                                    );
                                })}
                            </tr>
                        </thead>
                    }
                    <tbody>
                        {sortedTableRows(props.rows)
                            .map((row: ITableRowProps, i: number) => (
                                renderRow(row, i, 0, true, checkIfSiblingHasChildren(hasTableData(props.rows) ? props.rows : undefined))
                            ))}
                    </tbody>
                </table>
                : <DataInfo {...props.rows} />
            }
        </div>
    );

    if (props.limitHeight) {
        return (
            <Scroll className="table__scroll-fade">
                {renderTable()}
            </Scroll>
        );
    } else {
        return renderTable();
    }
};

export const cellTooltip = (children: React.ReactNode, tooltipContent: React.ReactNode): React.ReactNode => (<Dropdown
    delayShow={200}
    offset={[0, 17]}
    trigger={{
        children,
    }}
    isHidden={true}
>
    {tooltipContent}
</Dropdown>);
